import { useTranslation } from "../../../../../hooks/useTranslation";
import { AddRestrictionButton } from "../../../components/AddRestrictionButton";
import { useGetLocale } from "@refinedev/core";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { Box, Typography } from "@mui/material";
import {
  getFilters,
  getStandartColumns,
} from "../../../components/standartListData";
import { PaginatedList } from "../../../../../components/form/PaginatedList/PaginatedList";

type RestrictedProduct = {
  id: string;
  asin: string;
  productName: string;
  brandName: string;
  productImageUrl: string;
  restrictionReason: string;
  infringementType: string;
  note: string;
  created: string;
};

export const RestrictedProducts = () => {
  const { translate } = useTranslation(
    "pages.storeSecurity.restrictions.restrictedProducts",
  );

  const locale = useGetLocale();
  const filters = useMemo(() => getFilters(translate), [translate]);

  const columns = useMemo<GridColDef<RestrictedProduct>[]>(
    () => [
      {
        field: "productImageUrl",
        align: "center",
        sortable: false,
        headerName: translate("list.fields.productImageUrl"),
        renderCell: (params) =>
          params.value ? (
            <img src={params.value} alt="product" />
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <NoPhotographyIcon />
            </Box>
          ),
      },
      {
        field: "productName",
        sortable: false,
        headerName: translate("list.fields.productName"),
        width: 300,
        renderCell: (params) => (
          <Typography
            variant="body2"
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflow: "elipsis",
            }}
          >
            {params.formattedValue}
          </Typography>
        ),
        valueFormatter: (value: string) =>
          value ?? translate("list.fields.noProductName"),
      },
      {
        field: "asin",
        headerName: translate("list.fields.asin"),
        valueFormatter: (value: string) => value.toUpperCase() ?? "-",
        renderCell: (params) => {
          return params.value ? (
            <Typography
              component="a"
              href={`https://www.amazon.com/dp/${params.value}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              color="primary"
              noWrap
              sx={{ cursor: "pointer", textDecoration: "none" }}
            >
              {params.value.toUpperCase()}
            </Typography>
          ) : (
            ""
          );
        },
        width: 150,
      },
      {
        field: "brandName",
        sortable: false,
        headerName: translate("list.fields.brandName"),
        valueFormatter: (value: string) => value ?? "-",
        width: 150,
      },
      ...getStandartColumns(translate, locale()),
    ],
    [locale],
  );

  return (
    <PaginatedList
      addButton={
        <AddRestrictionButton
          translationPrefix="pages.storeSecurity.restrictions.restrictedProducts.addDialog"
          endpoint="store/restrictions/product"
          hasDescription
        />
      }
      filters={filters}
      defaultFilters={{
        search: "",
        restrictionReason: "",
        infringementType: "",
      }}
      getUrl={(countryId) => `store/${countryId}/restrictions/product`}
      columns={columns}
      rowHeight={64}
      searchLabel={translate("list.filters.search")}
    />
  );
};
